import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import {
  sendSignInLinkToEmail,
  isSignInWithEmailLink,
  signInWithEmailLink,
} from "firebase/auth";
import { getlostFirebaseAuth } from "../../utils/auth/firebaseConfig";
import { PhoneAuth } from "./PhoneAuth";

interface EmailVerificationProps {
  onSuccess: () => void;
  onError: (error: Error) => void;
  onNeedsOnboarding?: () => void;
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const MAGIC_LINK_REDIRECT_URL =
  import.meta.env.VITE_MAGIC_LINK_REDIRECT_URL || `${window.location.origin}/login`;

const validateEmail = (email: string): boolean => {
  return EMAIL_REGEX.test(email);
};

const completeEmailSignIn = async (
  email: string,
  onSuccess: () => void,
  onError: (error: Error) => void,
  onNeedsOnboarding?: () => void
) => {
  try {
    const result = await signInWithEmailLink(getlostFirebaseAuth.auth, email, window.location.href);
    const user = result.user;

    // Email is verified; now check if phone is also present
    if (!user.phoneNumber) {
      // No phone number; user needs to add one
      // Return false to indicate phone is needed
      return false;
    }

    // Both email and phone present; establish admin session
    try {
      await getlostFirebaseAuth.establishAdminSession();
      onSuccess();
    } catch (sessionErr: any) {
      console.error("Session establishment error:", sessionErr);
      const msg = sessionErr?.message ?? "";
      if (msg.includes("404") && msg.includes("Profile not found")) {
        onNeedsOnboarding?.();
      } else {
        onSuccess(); // Still consider it success; credentials are valid
      }
    }

    return true;
  } catch (err: any) {
    console.error("Error completing email sign-in:", err);
    onError(err);
    throw err;
  }
};

export const EmailVerification: React.FC<EmailVerificationProps> = ({
  onSuccess,
  onError,
  onNeedsOnboarding,
}) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [emailSent, setEmailSent] = useState(false);
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [promptEmail, setPromptEmail] = useState("");
  const [needsPhoneNumber, setNeedsPhoneNumber] = useState(false);
  const [verifiedEmail, setVerifiedEmail] = useState<string>("");

  const handleSendVerification = async (emailToSend?: string) => {
    const emailAddress = emailToSend || email;

    if (!emailAddress || !validateEmail(emailAddress)) {
      setError("Please enter a valid email address");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const actionCodeSettings = {
        url: `${MAGIC_LINK_REDIRECT_URL}?email=${encodeURIComponent(emailAddress)}`,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(getlostFirebaseAuth.auth, emailAddress, actionCodeSettings);

      // Save the email locally so we can complete sign-in later
      localStorage.setItem("emailForSignIn", emailAddress);
      setEmailSent(true);
    } catch (err: any) {
      console.error("Error sending verification email:", err);
      setError(err?.message || "Failed to send verification email. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendEmail = () => {
    setEmailSent(false);
    const originalEmail = localStorage.getItem("emailForSignIn") || email;
    setEmail(originalEmail);
    handleSendVerification(originalEmail);
  };

  const handleEmailPromptSubmit = async () => {
    if (!validateEmail(promptEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    setShowEmailPrompt(false);
    setLoading(true);
    setError(null);

    try {
      const result = await signInWithEmailLink(getlostFirebaseAuth.auth, promptEmail, window.location.href);
      localStorage.removeItem("emailForSignIn");
      setVerifiedEmail(promptEmail);

      // Check if phone is present
      if (!result.user.phoneNumber) {
        setNeedsPhoneNumber(true);
      } else {
        // Both present; establish session
        await completeEmailSignIn(promptEmail, onSuccess, onError, onNeedsOnboarding);
      }
    } catch (err: any) {
      console.error("Error verifying email:", err);
      setError(err?.message || "Failed to verify email");
    } finally {
      setLoading(false);
    }
  };

  const handlePhoneSuccess = async () => {
    setNeedsPhoneNumber(false);
    // Phone has been linked and verified; establish admin session
    try {
      await getlostFirebaseAuth.establishAdminSession();
      onSuccess();
    } catch (err: any) {
      console.error("Error after phone verification:", err);
      const msg = err?.message ?? "";
      if (msg.includes("404") && msg.includes("Profile not found")) {
        onNeedsOnboarding?.();
      } else {
        onError(err);
      }
    }
  };

  // Check for email link on component mount
  useEffect(() => {
    if (isSignInWithEmailLink(getlostFirebaseAuth.auth, window.location.href)) {
      console.log("Magic link detected, processing sign-in...");
      console.log("Current URL:", window.location.href);
      
      let emailForSignIn = localStorage.getItem("emailForSignIn");
      console.log("Email from localStorage:", emailForSignIn);

      if (!emailForSignIn) {
        // Try to get email from URL query parameters
        const urlParams = new URLSearchParams(window.location.search);
        console.log("URL search params:", window.location.search);
        const emailFromUrl = urlParams.get("email");
        console.log("Email from URL params:", emailFromUrl);
        
        if (emailFromUrl && validateEmail(emailFromUrl)) {
          // Use email from URL if valid
          emailForSignIn = decodeURIComponent(emailFromUrl);
          console.log("Using email from URL:", emailForSignIn);
          // Store it for potential retry
          localStorage.setItem("emailForSignIn", emailForSignIn);
        } else {
          console.log("No valid email found in localStorage or URL, showing prompt");
          // Show email prompt dialog only if we couldn't get it from URL
          setShowEmailPrompt(true);
          return;
        }
      }

      console.log("Proceeding with email sign-in for:", emailForSignIn);
      setLoading(true);
      completeEmailSignIn(emailForSignIn, onSuccess, onError, onNeedsOnboarding)
        .then((hasPhone) => {
          localStorage.removeItem("emailForSignIn");
          if (!hasPhone) {
            // No phone; show phone collection inline
            setVerifiedEmail(emailForSignIn);
            setNeedsPhoneNumber(true);
          }
        })
        .catch((err) => {
          console.error("Error during email sign-in:", err);
          // Show prompt on error so user can retry
          setShowEmailPrompt(true);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  }, [onSuccess, onError, onNeedsOnboarding]);

  // If phone number is needed after email verification, show phone collection inline
  if (needsPhoneNumber) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            Email verified! Now we need your phone number to complete your account setup.
          </Typography>
        </Alert>
        <PhoneAuth
          onSuccess={handlePhoneSuccess}
          onError={onError}
          onNeedsOnboarding={onNeedsOnboarding}
          isLinking={true}
        />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, width: "100%" }}>
        {error && <Alert severity="error">{error}</Alert>}

        {!emailSent ? (
          <>
            <Typography variant="body2" color="textSecondary">
              Sign in with your email address
            </Typography>
            <TextField
              label="Email Address"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={loading}
              fullWidth
              size="small"
              placeholder="your.email@example.com"
              error={email.length > 0 && !validateEmail(email)}
              helperText={email.length > 0 && !validateEmail(email) ? "Invalid email address" : ""}
            />
            <Button
              onClick={() => handleSendVerification()}
              disabled={loading || !email || !validateEmail(email)}
              fullWidth
              variant="contained"
            >
              {loading ? <CircularProgress size={20} /> : "Send Magic Link"}
            </Button>
          </>
        ) : (
          <>
            <Alert severity="success">
              <Typography variant="body2">
                Magic link sent to <strong>{email}</strong>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, fontSize: "0.85rem" }}>
                Check your email and click the link to sign in
              </Typography>
            </Alert>
            <Button
              onClick={handleResendEmail}
              disabled={loading}
              fullWidth
              variant="outlined"
            >
              Resend Link
            </Button>
            <Button
              onClick={() => {
                setEmailSent(false);
                setEmail("");
                localStorage.removeItem("emailForSignIn");
              }}
              disabled={loading}
              fullWidth
              variant="text"
            >
              Use Different Email
            </Button>
          </>
        )}
      </Box>

      {/* Email confirmation dialog (when returning from link without stored email) */}
      <Dialog open={showEmailPrompt} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Email</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 1 }}>
            Please provide the email address you used for sign-in
          </Typography>
          <TextField
            label="Email Address"
            type="email"
            value={promptEmail}
            onChange={(e) => setPromptEmail(e.target.value)}
            fullWidth
            placeholder="your.email@example.com"
            sx={{ mt: 2 }}
            autoFocus
            size="small"
            error={promptEmail.length > 0 && !validateEmail(promptEmail)}
            helperText={promptEmail.length > 0 && !validateEmail(promptEmail) ? "Invalid email address" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleEmailPromptSubmit}
            variant="contained"
            disabled={!validateEmail(promptEmail) || loading}
          >
            {loading ? <CircularProgress size={20} /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

// Email Collection Dialog for collecting email from users without email
interface EmailCollectionDialogProps {
  open: boolean;
  onSubmit: (email: string) => void;
  onClose?: () => void;
  loading?: boolean;
}

export const EmailCollectionDialog: React.FC<EmailCollectionDialogProps> = ({
  open,
  onSubmit,
  onClose,
  loading = false,
}) => {
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    if (!email || !validateEmail(email)) {
      setError("Please enter a valid email address");
      return;
    }
    setError(null);
    onSubmit(email);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Email Required</DialogTitle>
      <DialogContent>
        <Typography variant="body1" gutterBottom>
          We need your email address to complete your account setup.
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {error}
          </Alert>
        )}
        <TextField
          label="Email Address"
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            setError(null);
          }}
          fullWidth
          disabled={loading}
          placeholder="your.email@example.com"
          sx={{ mt: 2 }}
          autoFocus
          error={email.length > 0 && !validateEmail(email)}
          helperText={email.length > 0 && !validateEmail(email) ? "Invalid email address" : ""}
        />
      </DialogContent>
      <DialogActions>
        {onClose && (
          <Button onClick={onClose} disabled={loading}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || !email || !validateEmail(email)}
        >
          {loading ? <CircularProgress size={24} /> : "Submit"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
